import React, { useMemo, useState } from 'react';
import { useData } from '../../store/DataContext';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Input } from '../../components/ui/Input';
import { Select } from '../../components/ui/Select';
import { Badge } from '../../components/ui/Badge';
import { Patient } from '../../types';
import { Users, UserPlus, Search, Calendar, Clock, CheckCircle, AlertCircle, Stethoscope, Baby, FileText, ArrowRight } from 'lucide-react';
import { PatientReports } from './PatientReports';
import { PatientReviewDueList } from './PatientReviewDueList';
interface Props {}
export const ReceptionDashboard: React.FC<Props> = () => {
  const {
    patients,
    addPatient,
    encounters,
    createEncounter,
    currentUser,
    getPatientEncounters,
    addMaternityFile,
    maternityFiles
  } = useData();
  const [activeTab, setActiveTab] = useState<'register' | 'queue' | 'reports' | 'reviewDue' | 'maternity'>('register');
  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedPatient, setSelectedPatient] = useState<Patient | null>(null);
  // Form state
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    sex: 'Male' as 'Male' | 'Female',
    age: '',
    maritalStatus: 'Single' as 'Married' | 'Single',
    address: '',
    contact: '',
    occupation: '',
    nextOfKin: '',
    nokContact: '',
    nokRelationship: '',
    allergies: '',
    hivStatus: 'Unknown' as 'Positive' | 'Negative' | 'Unknown',
    isVitalsCompleted: false
  });
  // Maternity Form State
  const [maternityForm, setMaternityForm] = useState({
    firstName: '',
    surname: '',
    otherNames: '',
    age: '',
    religion: '',
    churchAttended: '',
    residentialAddress: '',
    locationLandmark: '',
    phone: '',
    nokName: '',
    nokPhone: '',
    nokRelationship: '',
    spouseFirstName: '',
    spouseSurname: '',
    spouseOtherNames: '',
    spouseOccupation: '',
    spouseContact: ''
  });
  // Computed properties
  const activeEncounters = useMemo(() => {
    const now = Date.now();
    const upper = now + 24 * 60 * 60 * 1000;
    return encounters.filter(e => {
      if (!e.reviewDate) return false;
      if (e.reviewStatus === 'Cancelled') return false;
      const t = new Date(e.reviewDate).getTime();
      if (Number.isNaN(t)) return false;
      return t <= upper; // includes overdue (t < now) AND future-within-24h
    }).length;
  }, [encounters]);
  const filteredPatients = patients.filter(p => p.firstName.toLowerCase().includes(searchQuery.toLowerCase()) || p.lastName.toLowerCase().includes(searchQuery.toLowerCase()) || p.bmcFileNumber.toLowerCase().includes(searchQuery.toLowerCase()));
  const handleRegister = (e: React.FormEvent) => {
    e.preventDefault();
    const newPatient = addPatient({
      ...formData,
      age: parseInt(formData.age) || 0
    });
    // Auto create encounter and send to nursing
    createEncounter(newPatient.id);
    // Reset form and show success
    setFormData({
      firstName: '',
      lastName: '',
      sex: 'Male',
      age: '',
      maritalStatus: 'Single',
      address: '',
      contact: '',
      occupation: '',
      nextOfKin: '',
      nokContact: '',
      nokRelationship: '',
      allergies: '',
      hivStatus: 'Unknown'
    });
    alert(`Patient registered successfully! BMC Number: ${newPatient.bmcFileNumber}. Sent to Nursing.`);
    setActiveTab('search');
    setSearchQuery(newPatient.bmcFileNumber);
  };
  const handleRegisterMaternity = (e: React.FormEvent) => {
    e.preventDefault();
    if (!currentUser) return;
    const now = new Date();
    const yy = String(now.getFullYear()).slice(-2);
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const prefix = `MAT-${yy}${mm}`;
    const monthCount = maternityFiles.filter(m => m.bmcFileNumber.startsWith(prefix)).length;
    const sequence = String(monthCount + 1).padStart(3, '0');
    const bmcFileNumber = `${prefix}${sequence}`;
    addMaternityFile({
      bmcFileNumber,
      createdAt: new Date().toISOString(),
      createdBy: currentUser.id,
      stage: 'NURSING',
      patient: {
        firstName: maternityForm.firstName,
        surname: maternityForm.surname,
        otherNames: maternityForm.otherNames,
        age: maternityForm.age,
        religion: maternityForm.religion,
        churchAttended: maternityForm.churchAttended,
        residentialAddress: maternityForm.residentialAddress,
        locationLandmark: maternityForm.locationLandmark,
        phone: maternityForm.phone,
        nextOfKin: {
          name: maternityForm.nokName,
          phone: maternityForm.nokPhone,
          relationship: maternityForm.nokRelationship
        }
      },
      spouse: {
        firstName: maternityForm.spouseFirstName,
        surname: maternityForm.spouseSurname,
        otherNames: maternityForm.spouseOtherNames,
        occupation: maternityForm.spouseOccupation,
        contact: maternityForm.spouseContact
      },
      admission: {
        date: '',
        time: ''
      },
      generalExam: {
        temp: '',
        pulse: '',
        respiration: '',
        bp: '',
        oedema: '',
        anemia: '',
        cardiovascularSystem: '',
        respiratorySystem: '',
        centralNervousSystem: '',
        breasts: '',
        varicosities: '',
        vaginalDischarge: '',
        otherAbnormalities: '',
        msUrine: {
          sg: '',
          albumen: '',
          acetone: '',
          sugar: ''
        },
        hb: '',
        abo: '',
        rh: ''
      },
      presentPregnancy: {
        lmp: '',
        quickening: '',
        edd: '',
        gestationAge: '',
        ancSummaryFirstBp: '',
        ancSummaryAtWeeks: '',
        syphilisTest: {
          date: '',
          result: '',
          treatment: ''
        },
        hpTest: {
          date: '',
          result: ''
        },
        pelvicAssessment: '',
        abnormalities: ''
      },
      labourObservations: [],
      delivery: {
        laceration: '',
        anterior: '',
        posterior: '',
        none: '',
        repaired: '',
        membranes: '',
        membranesDate: '',
        membranesTime: '',
        episiotomy: '',
        episiotomyRepaired: '',
        maternalARV: '',
        arvTypes: '',
        uterineMassage: '',
        motherOnHAART: '',
        cytotec: '',
        syntometrine: '',
        firstStageBeganDate: '',
        firstStageBeganTime: '',
        placentaDelivery: '',
        completeDilationDate: '',
        completeDilationTime: '',
        placentaDeliveredDate: '',
        placentaDeliveredTime: '',
        abnormalityAtDilation: '',
        abnormalityAtDilationDesc: '',
        postpartumHemorrhage: '',
        deliveryMethod: '',
        ebl: '',
        abnormalityAtDelivery: '',
        abnormalityAtDeliveryDesc: '',
        complications: '',
        deliverySummary: []
      },
      newbornEvaluation: {
        ega: '',
        alive: '',
        neonatalDeath: '',
        causeOfDeath: '',
        apgar1: '',
        apgar5: '',
        apgar10: '',
        generalExam: '',
        congenitalMalformation: '',
        congenitalMalformationDesc: '',
        infantARVs: '',
        infantFeeding: '',
        resuscitationMethods: '',
        crownHeelLength: '',
        headCircumference: '',
        skinJaundice: '',
        skinOther: '',
        fontanelles: '',
        eyes: '',
        eyesTreatment: '',
        mouth: '',
        neck: '',
        chest: '',
        abdomen: '',
        umbilicus: '',
        genitalia: '',
        urine: '',
        anus: '',
        meconium: '',
        upperLimbs: '',
        erbParalysis: '',
        lowerLimbs: '',
        ortolaniSign: '',
        back: '',
        reflexesGrasp: '',
        reflexesMoro: '',
        reflexesSucking: '',
        progressNotes: ''
      },
      postDeliveryObservations: [],
      puerperiumNotes: []
    });
    setMaternityForm({
      firstName: '',
      surname: '',
      otherNames: '',
      age: '',
      religion: '',
      churchAttended: '',
      residentialAddress: '',
      locationLandmark: '',
      phone: '',
      nokName: '',
      nokPhone: '',
      nokRelationship: '',
      spouseFirstName: '',
      spouseSurname: '',
      spouseOtherNames: '',
      spouseOccupation: '',
      spouseContact: ''
    });
    alert(`Maternity patient registered successfully. File Number: ${bmcFileNumber}. File forwarded to Nursing.`);
  };
  const handleAddToQueue = (patientId: string) => {
    createEncounter(patientId);
    alert('Patient added to queue successfully.');
  };
  return <div className="space-y-6" data-id="element-4970">
      <div className="flex justify-between items-center" data-id="element-4971">
        <h1 className="text-2xl font-bold text-gray-900" data-id="element-4972">
          Reception Dashboard
        </h1>
        <nav className="flex space-x-2" data-id="element-4973">
          <button onClick={() => setActiveTab('register')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'register' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4974">
            <UserPlus className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4975" />
            Register New
          </button>
          <button onClick={() => setActiveTab('queue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'queue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4976">
            <Users className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4977" />
            Queue
          </button>
          <button onClick={() => setActiveTab('reports')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reports' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4978">
            <FileText className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4979" />
            Patient Reports
          </button>
          <button onClick={() => setActiveTab('reviewDue')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'reviewDue' ? 'bg-white text-blue-600 border-t-2 border-blue-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4980">
            Review Due
          </button>
          <button onClick={() => setActiveTab('maternity')} className={`px-4 py-2 font-medium text-sm rounded-t-lg ${activeTab === 'maternity' ? 'bg-white text-pink-600 border-t-2 border-pink-600' : 'text-gray-500 hover:text-gray-700'}`} data-id="element-4981">
            <Baby className="w-4 h-4 inline-block mr-2 mb-1" data-id="element-4982" />
            Maternity Registration
          </button>
        </nav>
      </div>

      {activeTab === 'register' && <Card title="Register New Patient" data-id="element-4983">
          <form onSubmit={handleRegister} className="space-y-6" data-id="element-4984">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4985">
              <Input label="First Name" required value={formData.firstName} onChange={e => setFormData({
            ...formData,
            firstName: e.target.value
          })} data-id="element-4986" />
              <Input label="Last Name" required value={formData.lastName} onChange={e => setFormData({
            ...formData,
            lastName: e.target.value
          })} data-id="element-4987" />
              <Input label="Age" type="number" required value={formData.age} onChange={e => setFormData({
            ...formData,
            age: e.target.value
          })} data-id="element-4988" />

              <Select label="Sex" options={[{
            value: 'Male',
            label: 'Male'
          }, {
            value: 'Female',
            label: 'Female'
          }]} required value={formData.sex} onChange={e => setFormData({
            ...formData,
            sex: e.target.value as any
          })} data-id="element-4989" />
              <Select label="Marital Status" options={[{
            value: 'Single',
            label: 'Single'
          }, {
            value: 'Married',
            label: 'Married'
          }]} required value={formData.maritalStatus} onChange={e => setFormData({
            ...formData,
            maritalStatus: e.target.value as any
          })} data-id="element-4990" />
              <Input label="Contact Number" required value={formData.contact} onChange={e => setFormData({
            ...formData,
            contact: e.target.value
          })} data-id="element-4991" />

              <Input label="Home Address" className="md:col-span-2" required value={formData.address} onChange={e => setFormData({
            ...formData,
            address: e.target.value
          })} data-id="element-4992" />
              <Input label="Occupation" value={formData.occupation} onChange={e => setFormData({
            ...formData,
            occupation: e.target.value
          })} data-id="element-4993" />
            </div>

            <div className="border-t border-gray-200 pt-4" data-id="element-4994">
              <h4 className="text-md font-medium text-gray-900 mb-4" data-id="element-4995">
                Emergency Contact
              </h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-4996">
                <Input label="Next of Kin Name" required value={formData.nextOfKin} onChange={e => setFormData({
              ...formData,
              nextOfKin: e.target.value
            })} data-id="element-4997" />
                <Input label="Relationship" required value={formData.nokRelationship} onChange={e => setFormData({
              ...formData,
              nokRelationship: e.target.value
            })} data-id="element-4998" />
                <Input label="Contact Number" required value={formData.nokContact} onChange={e => setFormData({
              ...formData,
              nokContact: e.target.value
            })} data-id="element-4999" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5000">
              <Button type="submit" size="lg" data-id="element-5001">
                Register & Send to Nursing
              </Button>
            </div>
          </form>
        </Card>}

      {activeTab === 'queue' && <div className="space-y-6" data-id="element-5002">
          <Card data-id="element-5003">
            <div className="relative" data-id="element-5004">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-5005">
                <Search className="h-5 w-5 text-gray-400" data-id="element-5006" />
              </div>
              <input type="text" className="block w-full pl-10 pr-3 py-3 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm" placeholder="Search by name or BMC number..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} data-id="element-5007" />
            </div>
          </Card>

          {searchQuery && !selectedPatient && <Card noPadding data-id="element-5008">
              <ul className="divide-y divide-gray-200" data-id="element-5009">
                {filteredPatients.length > 0 ? filteredPatients.map(patient => <li key={patient.id} className="p-4 hover:bg-gray-50 cursor-pointer flex justify-between items-center" onClick={() => setSelectedPatient(patient)} data-id="element-5010">
                      <div data-id="element-5011">
                        <p className="text-sm font-medium text-blue-600" data-id="element-5012">
                          {patient.bmcFileNumber}
                        </p>
                        <p className="text-lg font-bold text-gray-900" data-id="element-5013">
                          {patient.firstName} {patient.lastName}
                        </p>
                        <p className="text-sm text-gray-500" data-id="element-5014">
                          {patient.age} yrs • {patient.sex} • {patient.contact}
                        </p>
                      </div>
                      <ArrowRight className="h-5 w-5 text-gray-400" data-id="element-5015" />
                    </li>) : <li className="p-8 text-center text-gray-500" data-id="element-5016">
                    No patients found matching "{searchQuery}"
                  </li>}
              </ul>
            </Card>}

          {selectedPatient && <div className="space-y-6" data-id="element-5017">
              <Button variant="ghost" onClick={() => setSelectedPatient(null)} className="mb-2" data-id="element-5018">
                ← Back to search results
              </Button>

              <Card title="Patient Details" action={<Button onClick={() => handleAddToQueue(selectedPatient.id)} data-id="element-5020">
                    Add to Queue
                  </Button>} data-id="element-5019">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6" data-id="element-5021">
                  <div data-id="element-5022">
                    <p className="text-sm text-gray-500" data-id="element-5023">BMC Number</p>
                    <p className="font-medium" data-id="element-5024">
                      {selectedPatient.bmcFileNumber}
                    </p>
                  </div>
                  <div data-id="element-5025">
                    <p className="text-sm text-gray-500" data-id="element-5026">Full Name</p>
                    <p className="font-medium" data-id="element-5027">
                      {selectedPatient.firstName} {selectedPatient.lastName}
                    </p>
                  </div>
                  <div data-id="element-5028">
                    <p className="text-sm text-gray-500" data-id="element-5029">Age / Sex</p>
                    <p className="font-medium" data-id="element-5030">
                      {selectedPatient.age} / {selectedPatient.sex}
                    </p>
                  </div>
                  <div data-id="element-5031">
                    <p className="text-sm text-gray-500" data-id="element-5032">Contact</p>
                    <p className="font-medium" data-id="element-5033">{selectedPatient.contact}</p>
                  </div>
                  <div data-id="element-5034">
                    <p className="text-sm text-gray-500" data-id="element-5035">Address</p>
                    <p className="font-medium" data-id="element-5036">{selectedPatient.address}</p>
                  </div>
                  <div data-id="element-5037">
                    <p className="text-sm text-gray-500" data-id="element-5038">Next of Kin</p>
                    <p className="font-medium" data-id="element-5039">
                      {selectedPatient.nextOfKin} ({selectedPatient.nokContact})
                    </p>
                  </div>
                  <div data-id="element-5040">
                    <p className="text-sm text-gray-500" data-id="element-5041">Allergies</p>
                    <p className="font-medium text-red-600" data-id="element-5042">
                      {selectedPatient.allergies}
                    </p>
                  </div>
                </div>

                <div className="border-t border-gray-200 pt-6" data-id="element-5043">
                  <h4 className="text-lg font-medium text-gray-900 mb-4 flex items-center" data-id="element-5044">
                    <Clock className="w-5 h-5 mr-2 text-gray-500" data-id="element-5045" /> Visit
                    History
                  </h4>

                  {getPatientEncounters(selectedPatient.id).length > 0 ? <div className="space-y-3" data-id="element-5046">
                      {getPatientEncounters(selectedPatient.id).map(enc => <div key={enc.id} className="bg-gray-50 p-4 rounded-lg border border-gray-200 flex justify-between items-center" data-id="element-5047">
                          <div data-id="element-5048">
                            <p className="font-medium text-gray-900" data-id="element-5049">
                              {new Date(enc.startedAt).toLocaleDateString()} at{' '}
                              {new Date(enc.startedAt).toLocaleTimeString()}
                            </p>
                            <p className="text-sm text-gray-500" data-id="element-5050">
                              {enc.lastAttendingClinician ? `Seen by ${enc.lastAttendingClinician}` : 'No clinician assigned'}
                            </p>
                          </div>
                          <Badge variant={enc.stage === 'COMPLETED' ? 'success' : 'warning'} data-id="element-5051">
                            {enc.stage}
                          </Badge>
                        </div>)}
                    </div> : <p className="text-gray-500 italic" data-id="element-5052">
                      No previous visits found.
                    </p>}
                </div>
              </Card>
            </div>}
        </div>}

      {activeTab === 'reports' && <PatientReports data-id="element-5053" />}

      {activeTab === 'reviewDue' && <PatientReviewDueList data-id="element-5054" />}

      {activeTab === 'maternity' && <Card title="Register Maternity Patient" data-id="element-5055">
          <form onSubmit={handleRegisterMaternity} className="space-y-6" data-id="element-5056">
            <div data-id="element-5057">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5058">
                Patient Details
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5059">
                <Input label="First Name" required value={maternityForm.firstName} onChange={e => setMaternityForm({
              ...maternityForm,
              firstName: e.target.value
            })} data-id="element-5060" />
                <Input label="Surname" required value={maternityForm.surname} onChange={e => setMaternityForm({
              ...maternityForm,
              surname: e.target.value
            })} data-id="element-5061" />
                <Input label="Other Names" value={maternityForm.otherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              otherNames: e.target.value
            })} data-id="element-5062" />
                <Input label="Age" type="number" required value={maternityForm.age} onChange={e => setMaternityForm({
              ...maternityForm,
              age: e.target.value
            })} data-id="element-5063" />
                <Input label="Phone Number" required value={maternityForm.phone} onChange={e => setMaternityForm({
              ...maternityForm,
              phone: e.target.value
            })} data-id="element-5064" />
                <Input label="Religion" value={maternityForm.religion} onChange={e => setMaternityForm({
              ...maternityForm,
              religion: e.target.value
            })} data-id="element-5065" />
                <Input label="Church Attended" value={maternityForm.churchAttended} onChange={e => setMaternityForm({
              ...maternityForm,
              churchAttended: e.target.value
            })} data-id="element-5066" />
                <div className="md:col-span-2" data-id="element-5067">
                  <Input label="Residential Address" required value={maternityForm.residentialAddress} onChange={e => setMaternityForm({
                ...maternityForm,
                residentialAddress: e.target.value
              })} data-id="element-5068" />
                </div>
                <div className="md:col-span-3" data-id="element-5069">
                  <Input label="Location / Landmark" value={maternityForm.locationLandmark} onChange={e => setMaternityForm({
                ...maternityForm,
                locationLandmark: e.target.value
              })} data-id="element-5070" />
                </div>
              </div>
            </div>

            <div data-id="element-5071">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5072">
                Next of Kin
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5073">
                <Input label="Name" required value={maternityForm.nokName} onChange={e => setMaternityForm({
              ...maternityForm,
              nokName: e.target.value
            })} data-id="element-5074" />
                <Input label="Phone" required value={maternityForm.nokPhone} onChange={e => setMaternityForm({
              ...maternityForm,
              nokPhone: e.target.value
            })} data-id="element-5075" />
                <Input label="Relationship" required value={maternityForm.nokRelationship} onChange={e => setMaternityForm({
              ...maternityForm,
              nokRelationship: e.target.value
            })} data-id="element-5076" />
              </div>
            </div>

            <div data-id="element-5077">
              <h3 className="text-lg font-medium text-gray-900 mb-4 border-b pb-2" data-id="element-5078">
                Spouse Details (Optional)
              </h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-5079">
                <Input label="First Name" value={maternityForm.spouseFirstName} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseFirstName: e.target.value
            })} data-id="element-5080" />
                <Input label="Surname" value={maternityForm.spouseSurname} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseSurname: e.target.value
            })} data-id="element-5081" />
                <Input label="Other Names" value={maternityForm.spouseOtherNames} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOtherNames: e.target.value
            })} data-id="element-5082" />
                <Input label="Occupation" value={maternityForm.spouseOccupation} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseOccupation: e.target.value
            })} data-id="element-5083" />
                <Input label="Contact" value={maternityForm.spouseContact} onChange={e => setMaternityForm({
              ...maternityForm,
              spouseContact: e.target.value
            })} data-id="element-5084" />
              </div>
            </div>

            <div className="flex justify-end pt-4" data-id="element-5085">
              <Button type="submit" size="lg" className="bg-pink-600 hover:bg-pink-700 text-white" data-id="element-5086">
                Register & Forward to Nursing
              </Button>
            </div>
          </form>
        </Card>}
    </div>;
};